import { Component, OnInit, OnDestroy, HostBinding, Inject } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { OverflowMenuComponent } from '../overflow-menu/overflow-menu.component';
import { FrameNavigationService } from '../shared/frame-navigation.service';
import { SubSink } from 'subsink';
import {
  ROUTE_LOADS,
  ROUTE_ROOT,
  ROUTE_PROFILE,
  ROUTE_JOBS,
  ROUTE_TRIPS,
  ROUTE_SHIPMENTS,
  ROUTE_DRIVER_DASHBOARD
} from 'src/app/shared/routes';
import { AppService } from 'src/app/app.service';
import { ProfileService } from 'src/app/profile/profile.service';
import { InformationDialogService } from 'src/app/shared/dialog/information-dialog.service';
import { Store } from '@ngxs/store';
import { RegistrationState } from 'src/app/registration/state/registration.state';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { Action } from 'src/app/shared/actions/models/action.model';
import { MatDialogRef } from '@angular/material/dialog';
import { InformationDialogComponent } from 'src/app/shared/dialog/information-dialog/information-dialog.component';
import { SplashStateService } from '../profile-splash/splash-state.service';

const PROFILE_STATUS_ICON = 'profile-status';
const NEED_INFO_TITLE = 'Need Profile Info';
const FOR_REVIEW_TITLE = 'Profile Under Review';
const DISMISS_BUTTON = 'Dismiss';
const DRIVER_TYPE_SERVICE_PROVIDER = 'driverServiceProvider';
class TabNavLink {
  title: string;
  icon: string;
  link: string;
  clickHandler: () => void;
  svgIcon: boolean;
  active: boolean;
  order: number;
}

@Component({
    selector: 'app-frame',
    templateUrl: './frame.component.html',
    styleUrls: ['./frame.component.scss'],
    standalone: false
})
export class FrameComponent implements OnInit, OnDestroy {
  @HostBinding('class.showNav') showNavBar = true;
  links: TabNavLink[] = [];
  activeRoute: string;
  driver;
  dispatcherConatct;
  showProfileSplash = false;
  private subs: SubSink = new SubSink();

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly bottomSheet: MatBottomSheet,
    private readonly frameNavigationService: FrameNavigationService,
    private readonly appService: AppService,
    private readonly profileService: ProfileService,
    private readonly informationDialogService: InformationDialogService,
    private readonly store: Store,
    private readonly registrationService: RegistrationService,
    private readonly splashStateService: SplashStateService,
    @Inject('environmentData') public environment: any
  ) {}

  ngOnInit() {
    this.driver = this.registrationService.getDriver();
    this.dispatcherConatct = this.registrationService.getDispatcherContact();
    this.determineMenuTabs();
    this.determineActiveLink();
    this.updateNavBarVisibility();
    if (this.appService.isNativeApp) {
      this.showNavBar = false;
    }
    this.subs.add(
      this.router.events.subscribe(val => {
        this.determineActiveLink();
        this.updateNavBarVisibility();
      })
    );

    // Subscribe to dispatcher contact changes to update menu tabs
    this.subs.add(
      this.store.select(RegistrationState.dispatcherContact).subscribe(dispatcherContact => {
        if (dispatcherContact && dispatcherContact.id) {
          this.dispatcherConatct = dispatcherContact;
          this.links = [];
          this.determineMenuTabs();
        }
      })
    );

    // Rebuild tabs when driver data updates (e.g. after completing profile steps)
    this.subs.add(
      this.store.select(RegistrationState.driver).subscribe(driver => {
        if (driver) {
          this.driver = driver;
          this.links = [];
          this.determineMenuTabs();
          this.determineActiveLink();
        }
      })
    );

    this.profileReviewDialog();
    this.needInfoDialog();

    this.subs.add(
      this.splashStateService.splashVisible$.subscribe(visible => {
        this.showProfileSplash = visible;
      })
    );
  }

  onSplashDismissed(): void {
    this.splashStateService.dismiss();
    this.router.navigateByUrl(`/${ROUTE_PROFILE}`);
  }
  determineMenuTabs(): void {
    this.links.push(
      {
        title: 'Profile',
        icon: 'profile-icon',
        link: `/${ROUTE_PROFILE}`,
        clickHandler: () => this.navigateToProfile(),
        svgIcon: true,
        order: 2
      } as TabNavLink,
      {
        title: 'More',
        icon: 'more_horiz',
        link: '',
        clickHandler: () => this.onOpenOverflowMenu(),
        svgIcon: false,
        order: 4
      } as TabNavLink,
    );

    this.links.push({
      title: 'Home',
      icon: 'home-icon',
      link: `/${ROUTE_DRIVER_DASHBOARD}`,
      clickHandler: () => this.navigateToDashboard(),
      svgIcon: true,
      order: 1
    } as TabNavLink);
    if (this.driver?.driverType === DRIVER_TYPE_SERVICE_PROVIDER && this.driver?.companyAssociatedId && this.environment?.featureFlags?.showTrips) {
      this.links.push({
        title: 'Trips',
        icon: 'car-icon',
        link: `/${ROUTE_TRIPS}`,
        clickHandler: () => this.navigateToTrips(),
        svgIcon: true,
        order: 3
      } as TabNavLink);
    }

    if (this.environment?.featureFlags?.isShipmentEnable && this.dispatcherConatct.id) {
      this.links.push({
        title: 'Shipments',
        icon: 'truck-line',
        link: `/${ROUTE_SHIPMENTS}`,
        clickHandler: () => this.navigateToShipments(),
        svgIcon: true,
        order: 3
      } as TabNavLink);
    }
    this.links.sort((a, b) => a.order - b.order);

    //For Future reference
    // this.links.push({
    //   title: 'Jobs',
    //   icon: 'jobs-icon',
    //   link: `/${ROUTE_JOBS}`,
    //   clickHandler: () => this.navigateToJobs(),
    //   svgIcon: true
    // } as TabNavLink);
    // this.links.push({
    //   title: 'Loads',
    //   icon: 'truck-icon',
    //   link: `/${ROUTE_LOADS}`,
    //   clickHandler: () => this.navigateToLoads(),
    //   svgIcon: true
    // } as TabNavLink);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onOpenOverflowMenu(): void {
    this.bottomSheet.open(OverflowMenuComponent);
  }

  private needInfoDialog(): void {
    if (this.profileService.isProfileInfoNeeded()) {
      const driver = this.store.selectSnapshot(RegistrationState.driver);
      const greeting = `Hi ${driver.firstName}!`;
      const infoMessage = 'We will need more information to clear your driver account.';
      const contactMessage = 'We will contact you asking the required information.';
      const action = this.getAction(DISMISS_BUTTON);

      const dialogRef = this.informationDialogService.openInformationDialog(
        NEED_INFO_TITLE,
        greeting,
        infoMessage,
        action,
        contactMessage,
        PROFILE_STATUS_ICON
      );
      action.action = () => this.closeDialog(dialogRef);
    }
  }

  private profileReviewDialog(): void {
    if (this.profileService.isProfileForReview()) {
      const driver = this.store.selectSnapshot(RegistrationState.driver);
      const greeting = `Hi ${driver.firstName}!`;
      const infoMessage =
        'Your profile is under review by our admins. Typically, it will take 2 business days to complete the review.';
      const contactMessage = 'We will send you an email when a decision has been made.';
      const action = this.getAction(DISMISS_BUTTON);
      const dialogRef = this.informationDialogService.openInformationDialog(
        FOR_REVIEW_TITLE,
        greeting,
        infoMessage,
        action,
        contactMessage,
        PROFILE_STATUS_ICON
      );
      action.action = () => this.closeDialog(dialogRef);
    }
  }

  private closeDialog(dialogRef: MatDialogRef<InformationDialogComponent>): any {
    dialogRef.close();
  }

  private getAction(buttonName: string): Action {
    return {
      name: buttonName,
      action: (data: any) => {}
    } as Action;
  }

  private navigateToProfile(): void {
    this.frameNavigationService.navigateToProfile();
  }

  private navigateToDashboard(): void {
    this.frameNavigationService.navigateToDriverDashboard();
  }

  private navigateToLoads(): void {
    this.frameNavigationService.navigateToLoads();
  }

  private navigateToTrips(): void {
    this.frameNavigationService.navigateToTrips();
  }

  private navigateToShipments(): void {
    this.frameNavigationService.navigateToShipments();
  }

  private navigateToJobs(): void {
    this.frameNavigationService.navigateToJobs();
  }

  private determineActiveLink(): void {
    this.links.forEach((link: TabNavLink) => (link.active = false));
    if (this.location.path() !== ROUTE_ROOT) {
      this.activeRoute = this.location.path();
      this.links.forEach((link: TabNavLink) => {
        if (link.link !== ROUTE_ROOT) {
          this.setActive(link);
        }
      });
    } else {
      this.activeRoute = ROUTE_ROOT;
      this.links[0].active = true;
    }
  }

  private setActive(link: TabNavLink): void {
    const checked = this.activeRoute.startsWith(link.link);
    link.active = checked;
  }

  private isProfileComplete(): boolean {
    if (!this.driver) return false;
    return !!(
      this.driver.licenseRequirementsCompleted &&
      this.driver.employmentRequirementsCompleted &&
      this.driver.residenceHistoryRequirementsCompleted &&
      this.driver.experienceRequirementsCompleted &&
      this.driver.accidentsRequirementsCompleted &&
      this.driver.convictionsRequirementsCompleted
    );
  }

  private updateNavBarVisibility(): void {
    // Hide nav bar on incidents, yard-inspection, and driver-time-card routes
    const currentPath = this.location.path();
    if (this.appService.isNativeApp) {
      this.showNavBar = false;
    } else {
      this.showNavBar = !currentPath.includes('/incidents') && !currentPath.includes('/yard-inspection') && !currentPath.includes('/driver-time-card');
    }
  }
}
